import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IScorecardKPI } from '../../shared/classes/scorecards-kpi-card-data-handler';
import { Options, SeriesPieOptions } from 'highcharts';
import { ReusableFunctions } from '../../shared/classes/reusable-functions';
import { ScorecardsChartOptions } from '../../shared/classes/scorecards-chart-options';
import { NoDataActionType, NoDataSourceType, NoDataTypes } from '../../shared/interfaces/no-data';
import * as Highcharts from 'highcharts';
import { ILargeCardInteriorOptions } from '../../shared/interfaces/card-data';
import { formatDate } from '@angular/common';
import { IScorecardDetailsCardData } from '../../shared/components/scorecards-score-summary-card/scorecards-score-summary-card.component';

@Injectable({
    providedIn: 'root',
})
export class PerformanceScorecardService {
    private serverUrl = 'https://' + window.location.host;
    private version = '0.5'; // TODO Get version number dynamically
    private _url = (this.serverUrl + '/api/v{version}/performancescorecard').replace(
        '{version}',
        this.version
    );

    constructor(private http: HttpClient, private reusableFunctions: ReusableFunctions) {}
    getPositionKpi(
        employeeIdentifier: string,
        scorecard: string,
        datePeriod: string,
        ymc: string
    ): Observable<IScorecardKPI[]> {
        let params: HttpParams = new HttpParams()
            .set('employeeIdentifier', employeeIdentifier)
            .set('scorecard', scorecard)
            .set('datePeriod', datePeriod)
            .set('ymc', ymc);

        return this.http.post<IScorecardKPI[]>(`${this._url}/kpi/employee`, {
            identifier: employeeIdentifier,
            scorecard: scorecard,
            datePeriod: datePeriod,
            ymc: ymc,
        });
    }

    getPositionMix(parameters: IScorecardParameters): Observable<ScorecardMixListWebModel> {
        const rc: Observable<ScorecardMixListWebModel> = this.http.post<ScorecardMixListWebModel>(
            `${this._url}/GetPositionScorecardProductMix`,
            { Identifier: JSON.stringify(parameters) }
        );
        return rc;
    }

    getEmployeeList(
        scorecard: string,
        datePeriod: string,
        ymc: string
    ): Observable<ScorecardEmployeeWebModel[]> {
        let params: HttpParams = new HttpParams()
            .set('scorecard', scorecard)
            .set('datePeriod', datePeriod)
            .set('ymc', ymc);

        const rc: Observable<ScorecardEmployeeWebModel[]> = this.http.get<
            ScorecardEmployeeWebModel[]
        >(`${this._url}/employees`, { params: params });

        return rc;
    }

    getProductionData(parameters: IScorecardParameters): Observable<ScorecardProductionWebModel[]> {
        const rc: Observable<ScorecardProductionWebModel[]> = this.http.post<
            ScorecardProductionWebModel[]
        >(`${this._url}/GetProductionCardData`, { Identifier: JSON.stringify(parameters) });
        return rc;
    }

    getScorecardPartnerList(
        thirdPartyType: string,
        fromDatePeriod: string,
        ymc: string
    ): Observable<ScorecardPartnerWebModel[]> {
        let params: HttpParams = new HttpParams()
            .set('thirdPartyType', thirdPartyType)
            .set('fromDatePeriod', fromDatePeriod)
            .set('ymc', ymc);

        const rc: Observable<ScorecardPartnerWebModel[]> = this.http.get<
            ScorecardPartnerWebModel[]
        >(`${this._url}/partner/partners`, { params: params });
        return rc;
    }

    getScoreDetails(parameters: IScorecardParameters): Observable<ScorecardDetailsWebModel> {
        const rc: Observable<ScorecardDetailsWebModel> = this.http.post<ScorecardDetailsWebModel>(
            `${this._url}/GetScoreDetails`,
            { Identifier: JSON.stringify(parameters) }
        );
        return rc;
    }

    getPartnerKpi(
        thirdPartyType: string,
        partnerIdentifier: string,
        fromDatePeriod: string,
        ymc: string
    ): Observable<IScorecardKPI[]> {
        let params: HttpParams = new HttpParams()
            .set('thirdPartyType', thirdPartyType)
            .set('partnerIdentifier', partnerIdentifier)
            .set('fromDatePeriod', fromDatePeriod)
            .set('ymc', ymc);

        return this.http.post<IScorecardKPI[]>(`${this._url}/partner/kpi`, {
            thirdPartyType: thirdPartyType,
            partnerIdentifier: partnerIdentifier,
            fromDatePeriod: fromDatePeriod,
            ymc: ymc,
        });
    }

    getEntityRolesByUser(): Observable<EntityHierarchy[]> {
        return this.http.get<EntityHierarchy[]>(this._url + '/entity/roles');
    }

    getEntityHierarchiesByRole(roleName: string): Observable<EntityHierarchy[]> {
        let params: HttpParams = new HttpParams().set('roleName', roleName);

        return this.http.get<EntityHierarchy[]>(this._url + '/entity/hierarchies', {
            params: params,
        });
    }

    getScorecardKPIs(
        roleName: string,
        hierarchyId: string,
        datePeriod: string,
        ymc: string
    ): Observable<IScorecardKPI[]> {
        let params: HttpParams = new HttpParams()
            .set('roleName', roleName)
            .set('hierarchyIdentifier', hierarchyId)
            .set('datePeriod', datePeriod)
            .set('ymc', ymc);

        return this.http.post<IScorecardKPI[]>(this._url + '/GetEntityScorecardKPIs', {
            roleName: roleName,
            hierarchyIdentifier: hierarchyId,
            fromDatePeriod: datePeriod,
            ymc: ymc,
        });
    }

    basePieOptions: SeriesPieOptions = {
        type: 'pie',
        dataLabels: {
            enabled: false,
        },
        showInLegend: false,
    };

    abbreviatePurpose(purpose: string): string {
        if (purpose) {
            switch (purpose.toLowerCase()) {
                case 'refinance':
                    return 'Refi';
                case 'purchase':
                case 'purch':
                    return 'Purch';
                case 'construction':
                    return 'Const';
                case 'modification':
                    return 'Modif';
                case 'other':
                    return 'Other';
                default:
                    return '';
            }
        }
    }

    getPieOptions(name: string, data: any[], showLegend: boolean): SeriesPieOptions {
        let ret = Object.assign({}, this.basePieOptions);
        ret.name = name;
        ret.data = data;
        ret.showInLegend = showLegend;
        return ret;
    }

    getLoanCategoryData(
        data: any[],
        category: string,
        type: string,
        property: string,
        chartGroup: string
    ): any[] {
        let legenIndices = this.reusableFunctions.getLegendIndex();
        let ret = data
            .filter((d) => d.category === category)
            .map((i) => {
                let abbrevation = i[property];
                let color = '';

                if (chartGroup === 'purpose') {
                    abbrevation = this.abbreviatePurpose(abbrevation);
                    color = this.reusableFunctions.getPurposeColor(abbrevation);
                } else {
                    color = this.reusableFunctions.getLoanTypeColor(abbrevation);
                }

                return {
                    y: i[type],
                    name: abbrevation,
                    color: color,
                    legendIndex: legenIndices[abbrevation],
                };
            });
        return ret;
    }

    setPositionMixPieChartData(
        data: ScorecardMixListWebModel,
        title: string,
        type: string,
        property: string,
        chartGroup: string
    ) {
        let pieChartOptions = [];
        let scorecardsChartOptions = new ScorecardsChartOptions();

        //Push graph data in the order it needs to be displayed
        let pieChartData: ScorecardMixWebModel[] = [];
        if (data?.pieDataMixList) {
            data.pieDataMixList.sort((a, b) => a.graphPosition - b.graphPosition);
            pieChartData = data.pieDataMixList;
        }
        let categories = this.reusableFunctions.getUniqueItemList(pieChartData, 'category');
        let showLegend = false;
        let sums = [];
        categories.forEach((cat) => {
            sums.push(
                pieChartData.filter((i) => i.category == cat).reduce((sum, i) => sum + i[type], 0)
            );
        });
        categories.forEach((cat, i) => {
            let graphData: SeriesPieOptions[] = [];
            graphData.push(
                this.getPieOptions(
                    cat,
                    this.getLoanCategoryData(pieChartData, cat, type, property, chartGroup),
                    showLegend
                )
            );

            let sumFormatted =
                type === 'units'
                    ? sums[i].toLocaleString()
                    : `$${this.reusableFunctions.formatLargeNumber(sums[i])}`;

            pieChartOptions.push(
                scorecardsChartOptions.loadProductMixChartOptions(
                    title,
                    true,
                    graphData,
                    sumFormatted,
                    cat.toUpperCase(),
                    type.toUpperCase()
                )
            );
            title = '';
            showLegend = !showLegend;
        });
        return pieChartOptions;
    }

    productPurposeMixCardLoad(
        data: ScorecardMixListWebModel,
        productionPurposeMixPieChartData: IProductionPurposeMixPieChartData,
        chartGroup: string
    ) {
        let pieChartOptions = this.setPositionMixPieChartData(
            data,
            chartGroup + ' Mix',
            chartGroup === 'Purpose' ? 'units' : 'volume',
            chartGroup === 'Purpose' ? 'purposeClass' : 'loanTypeCategory',
            chartGroup.toLowerCase()
        );

        productionPurposeMixPieChartData[chartGroup.toLowerCase() + 'MixChartLeft'] = Object.assign(
            {},
            pieChartOptions[0]
        );
        productionPurposeMixPieChartData[chartGroup.toLowerCase() + 'MixChartRight'] =
            Object.assign({}, pieChartOptions[1]);
        productionPurposeMixPieChartData['isLoading' + chartGroup + 'Mix'] = false;
        productionPurposeMixPieChartData.hasData = data?.pieDataMixList?.length > 0;
        return Object.assign({}, productionPurposeMixPieChartData);
    }

    initializeDonutcharts(): IProductionPurposeMixPieChartData {
        let noDataSelection = {
            action: NoDataActionType.emptySelect,
            searchTerm: '',
            componentTitle: 'Product/Purpose Mix',
            inputSource: NoDataSourceType.largeDataCard,
        };
        return {
            productMixChartLeft: {},
            productMixChartRight: {},
            purposeMixChartLeft: {},
            purposeMixChartRight: {},
            isLoadingProductMix: true,
            isLoadingPurposeMix: true,
            hasData: false,
            noDataType: noDataSelection,
        };
    }

    addSeriesOptions(
        name: string,
        data: number[],
        color: string,
        hasColumnData: boolean,
        seriesType: 'line' | 'column' | 'bar',
        milestones: string[]
    ) {
        let seriesOptions = {
            type: seriesType,
            yAxis: hasColumnData && seriesType === 'line' ? 1 : 0,
            legendIndex: milestones.indexOf(name),
            data: data,
            color: color,
            name: name,
        };
        return seriesOptions;
    }

    getMilestoneData(data: ScorecardProductionWebModel[], milestone: string, type: string) {
        let dataSelected: ScorecardProductionWebModel[] = data.filter(
            (d) => d.milestone === milestone
        );
        let pointDataList = [];
        dataSelected.forEach((dataItem) => {
            pointDataList.push({
                y: dataItem[type],
                properties: { dataType: type },
            });
        });
        return pointDataList;
    }

    addYAxis(name: string, opposite: boolean = false): Highcharts.YAxisOptions {
        return {
            title: {
                text: name,
                style: {
                    fontSize: '10px',
                    color: '#666666',
                },
            },
            opposite: opposite,
            labels: {
                enabled: true,
                style: {
                    fontSize: '10px',
                    color: '#666666',
                },
            },
            lineWidth: 1,
        };
    }

    initializeProductionChart(pageId: string, action: NoDataActionType): ILargeCardInteriorOptions {
        return Object.assign(
            {},
            {
                chart: null,
                componentTitle: '',
                isDataReady: false,
                hasData: false,
                showTitle: true,
                uniqueId: pageId + 'fundedProductionChart',
                chartTitleData: null,
                accessToUrl: '',
                action: action,
            }
        );
    }

    drawFundedProductionChart(
        data: ScorecardProductionWebModel[],
        largeCardInteriorOptions: ILargeCardInteriorOptions,
        chartTitle: string
    ) {
        if (data && data.length > 0) {
            let colors = ['#41D6C3', '#b870ff', '#FF9494', '#A6266E', '#0078D7'];
            let monthNames: string[] = [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
            ];
            let productionChartSeries: Highcharts.SeriesOptionsType[] = [];
            let yAxis: Highcharts.YAxisOptions[] = [];

            let milestones = this.reusableFunctions.getUniqueItemList(data, 'milestone'); //uniqueMilestones(data);
            const months = data
                .filter((d) => d.milestone === milestones[0])
                .map((d) => `${monthNames[d.prodMonth - 1]}`);

            //columns added first so the lines overlay the columns
            let colData = data.filter((d) => d.productionChartType == 0);
            let lineData = data.filter((d) => d.productionChartType == 1);

            let colMilestones =
                this.reusableFunctions.getUniqueItemList(colData, 'milestone') ?? [];
            let lineMilestones =
                this.reusableFunctions.getUniqueItemList(lineData, 'milestone') ?? [];

            let hasColumnData = colData.length > 0;
            let hasLineData = lineData.length > 0;

            colMilestones.forEach((milestone, i) => {
                productionChartSeries.push(
                    this.addSeriesOptions(
                        milestone,
                        this.getMilestoneData(data, milestone, 'volume'),
                        colors[i],
                        hasColumnData,
                        'column',
                        milestones
                    )
                );
            });

            lineMilestones.forEach((milestone, i) => {
                productionChartSeries.push(
                    this.addSeriesOptions(
                        milestone,
                        this.getMilestoneData(data, milestone, 'units'),
                        colors[colors.length - (i + 1)],
                        hasColumnData,
                        'line',
                        milestones
                    )
                );
            });

            if (hasColumnData) {
                yAxis.push(this.addYAxis('Volume ($)'));
            }
            if (hasLineData) {
                // TODO: make axisSecondaryTitle logic dynamic
                let axisSecondaryTitle = largeCardInteriorOptions.uniqueId.includes(
                    'performanceScorecardEntityType'
                )
                    ? '# Credit Pulls & Applications'
                    : '# units';
                yAxis.push(this.addYAxis(axisSecondaryTitle, hasColumnData));
            }
            let scorecardsChartOptions = new ScorecardsChartOptions();
            let fundedProductionChartOptions = scorecardsChartOptions.fundedProductionChartOptions(
                productionChartSeries,
                months,
                yAxis,
                chartTitle
            );

            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                fundedProductionChartOptions
            );
            largeCardInteriorOptions.hasData = true;
            largeCardInteriorOptions.isDataReady = true;
        } else {
            largeCardInteriorOptions.hasData = false;
            largeCardInteriorOptions.isDataReady = true;
        }
        return Object.assign({}, largeCardInteriorOptions);
    }

    initializeScoreDetailsCard(scoreCardType: ScorecardType, action: NoDataActionType) {
        let detailsCard: IScorecardDetailsCardData = {
            entityIdentifier: '',
            entityAbbr: '',
            action: action,
            hasData: false,
            isDataLoading: true,
            entity: '',
            scoreCardType: scoreCardType,
            entityPicture: null,
            entityTopLeftTitle: '',
            entityTopRightTitle: '',
            entityBotLeftTitle: '',
            entityBotRightTitle: '',
            entityTopLeftValue: '',
            entityTopRightValue: '',
            entityBotLeftValue: '',
            entityBotRightValue: '',
            rankTypeTitle: '',
            scoreValue: '',
            scoreValueOutOf: '',
            rankValue: '',
            rankValueOutOf: '',
            productTypeTitle: '',
            productVolumeValue: '',
            productVolumeValueOutOf: '',
            productUnitsValue: '',
            productUnitsValueOutOf: '',
        };
        return Object.assign({}, detailsCard);
    }

    updateScoreDetails(
        detailsCard: IScorecardDetailsCardData,
        scoreDetails: ScorecardDetailsWebModel
    ) {
        if (scoreDetails && scoreDetails !== null) {
            let entity = scoreDetails.entity;
            let entityTopLeftTitle = 'Primary Owner';
            let entityTopLeftValue = scoreDetails.entityManager;
            let entityTopRightTitle = 'Active Date';
            let entityTopRightValue = formatDate(
                scoreDetails.activeDate,
                'MM/dd/yyyy',
                'en-US',
                'UTC'
            );
            let rankTypeTitle = 'Weighted Stats & Ranks';

            if (detailsCard.scoreCardType === ScorecardType.employee) {
                entity = scoreDetails.employee;
                entityTopLeftTitle = 'Position';
                entityTopLeftValue = scoreDetails.position;
                entityTopRightTitle = 'Tenure';
                entityTopRightValue =
                    scoreDetails.tenureYr + ' yr    ' + scoreDetails.tenureMo + ' mo';
                rankTypeTitle = 'Weighted Score';
                detailsCard.entityAbbr = this.getEntityAbbr(entity);
            }

            let outOf = scoreDetails.outOf ? scoreDetails.outOf.toString() : '';

            detailsCard.entity = entity;
            detailsCard.entityTopLeftTitle = entityTopLeftTitle;
            detailsCard.entityTopLeftValue = entityTopLeftValue;
            detailsCard.entityTopRightTitle = entityTopRightTitle;
            detailsCard.entityTopRightValue = entityTopRightValue;
            detailsCard.entityBotLeftTitle = 'Location';
            detailsCard.entityBotLeftValue = scoreDetails.location;
            detailsCard.entityBotRightTitle = 'Best Month';
            detailsCard.entityBotRightValue = scoreDetails.bestMo;
            detailsCard.rankTypeTitle = rankTypeTitle;
            detailsCard.scoreValue = scoreDetails.score ? scoreDetails.score.toFixed(2) : '';
            detailsCard.scoreValueOutOf = '100';
            detailsCard.rankValue = scoreDetails.rank ? scoreDetails.rank.toString() : '';
            detailsCard.rankValueOutOf = outOf;
            detailsCard.productTypeTitle = scoreDetails.rankedBy
                ? scoreDetails.rankedBy + ' Production'
                : 'Funded Production';
            detailsCard.productVolumeValue = scoreDetails.volRank
                ? scoreDetails.volRank.toString()
                : '';
            detailsCard.productVolumeValueOutOf = outOf;
            detailsCard.productUnitsValue = scoreDetails.unitRank
                ? scoreDetails.unitRank.toString()
                : '';
            detailsCard.productUnitsValueOutOf = outOf;
            detailsCard.hasData = true;
            detailsCard.isDataLoading = false;
        }
        return Object.assign({}, detailsCard);
    }

    getEntityAbbr(entityName: string): string {
        if (entityName) {
            let nameTokens = entityName.split(' ');
            if (nameTokens.length > 2) {
                return nameTokens[0] + ' ' + nameTokens[nameTokens.length - 1];
            } else {
                return entityName;
            }
        }
        return '';
    }
}

export interface IProductionPurposeMixPieChartData {
    productMixChartLeft: Options;
    productMixChartRight: Options;
    purposeMixChartLeft: Options;
    purposeMixChartRight: Options;
    isLoadingProductMix: boolean;
    isLoadingPurposeMix: boolean;
    hasData: boolean;
    noDataType: NoDataTypes;
}

export class EntityHierarchy {
    roleName: string;
    roleLevel: string;
    hierarchyName: string;
    hierarchyIdentifier: string;
}

export class ScorecardEmployeeWebModel {
    employeeIdentifier: string;
    employee: string;
    forced: boolean;
}

export class ScorecardPartnerWebModel {
    partnerIdentifier: string;
    partnerName: string;
}

export class ScorecardDetailsWebModel {
    employee: string;
    position: string;
    tenureYr: number;
    tenureMo: number;
    location: string;
    bestMo: string;
    score: number;
    rank: number;
    volRank: number;
    unitRank: number;
    outOf: number;
    hallOfFame: boolean;
    circleOfExcellence: boolean;
    presidentsClub: boolean;
    goldstar: boolean;
    guid?: string;
    activeDate: Date;
    rankedBy: string;
    entity: string;
    entityManager: string;
    goldStar: number;
}

export class ScorecardProductionWebModel {
    prodYear: number;
    prodMonth: number;
    minDate: Date;
    milestone: string;
    units: number;
    volume: number;
    productionChartType: number;
    productionChartOrder: number;
}

export class ScorecardMixWebModel {
    purposeClass: string;
    category: string;
    units: number;
    volume: number;
    graphPosition: number;
    loanTypeCategory: string;
}

export class ScorecardMixListWebModel {
    pieDataMixList: ScorecardMixWebModel[];
    totalFundedVolume: number;
    totalFundedVolumeFormatted: string;
    totalLockedVolume: number;
    totalLockedVolumeFormatted: string;
}

export interface IScorecardParameters {
    scoreCardType: ScorecardType;
    entityIdentifier: string;
    entityType: string;
    fromDatePeriod: string;
    ymc: string;
    chartGroup?: string;
}

export enum ScorecardType {
    employee = 'employee',
    thirdParty = 'third-party',
    location = 'location',
}
